import React from 'react';
import { Breadcrumb as AntBreadcrumb } from 'antd';
import { Link, useLocation } from 'react-router-dom';
import { HomeOutlined } from '@ant-design/icons';
import { useTranslate } from '../../hooks/use-translate.hook';
import { dashboardRoutes } from '../../routes/config/dashboard.routes';

interface BreadcrumbProps {
  className?: string;
}

const Breadcrumb: React.FC<BreadcrumbProps> = ({ className }) => {
  const location = useLocation();
  const pathSnippets = location.pathname.split('/').filter((i) => i);
  const { t } = useTranslate();

  // Helper function to try multiple translation keys and return the first one that exists
  const translateWithFallback = (keys: string[]): string => {
    for (const key of keys) {
      const translation = t(key);
      // If the translation is different from the key, it means the key exists
      if (translation !== key) {
        return translation;
      }
    }
    // If no translation found, return the last key as fallback
    return keys[keys.length - 1];
  };

  // Find menu item by path
  const findMenuItemByPath = (path: string): { label: string; path: string } | null => {
    // First try to find direct match in routes
    for (const route of dashboardRoutes) {
      if (route.path === path) {
        return route;
      }

      // Check nested routes
      if (route.routes) {
        for (const childRoute of route.routes) {
          if (childRoute.path === path) {
            // Since nested routes don't have labels, return the parent route
            return route;
          }
        }
      }
    }

    // If no exact match, return null and use the path segment as label
    return null;
  };

  // Generate breadcrumb items
  const breadcrumbItems = [
    {
      title: (
        <Link to="/">
          <HomeOutlined />
        </Link>
      ),
      key: 'home',
    },
  ];

  // Build up the breadcrumbs based on URL segments
  let fullPath = '';
  pathSnippets.forEach((segment, index) => {
    fullPath += `/${segment}`;
    const menuItem = findMenuItemByPath(fullPath);

    const label = (menuItem?.label || segment).replace(/^./, (char: string) => char.toLowerCase());

    // Try multiple translation keys in order of preference
    const translatedLabel = translateWithFallback([
      `${label}.title`,
      `common.${label}`,
      label, // fallback to the original label
    ]);

    breadcrumbItems.push({
      title:
        index === pathSnippets.length - 1 ? (
          <span style={{ textTransform: 'capitalize' }}>{translatedLabel}</span>
        ) : (
          <Link to={fullPath} style={{ textTransform: 'capitalize' }}>
            {translatedLabel}
          </Link>
        ),
      key: fullPath,
    });
  });

  return <AntBreadcrumb items={breadcrumbItems} className={className} />;
};

export default Breadcrumb;
